import { useMemo, useState } from 'react';
import { Palmtree, Plus } from 'lucide-react';
import SelectBox from '../BaseComponents/SelectBox/SelectBox';
import TravelIconPicker from '../../screens/CMS/Layout/HomepageSettings/components/TravelIconPicker';
import { isEmoji } from '../../screens/CMS/Layout/HomepageSettings/components/travelIcons';
import { useToast } from '../../context/ToastContext';
import { MAX_MENU_ITEMS, MAX_SUB, emptyText, moveItem } from './siteHeaderModel';
import { WEBSITE_DEFAULTS, pickText } from './headerDefaults';
import { holidayTypeIcon } from './headerIcons';
import { holidayTypeRows, isActiveType } from './headerPreviewModel';
import {
  BilingualInput, BuiltInTile, FooterLinkEditor, RowActions, SectionCard, Segmented, TextField,
  useHeaderEditor,
} from './editorControls';
import { WebsiteShows } from './DestinationsMenuSection';
import styles from './HeaderEditor.module.css';

const PATH = 'menus.holidayTypes';

/**
 * The website header's "Holiday types" menu. Two ways to fill it:
 *  - Automatic: every ACTIVE holiday type, in the Holiday Types sort order.
 *  - Hand-picked: the types chosen here, in this order, each with its own label, sub-label and
 *    icon in both languages.
 *
 * Either way the website only ever receives active types: a pick that is switched off in
 * Holiday Types is kept here (flagged) but dropped from the site until it is switched back on.
 */
export default function HolidayTypesMenuSection({ value, set, patch, errorCount }) {
  const { showToast } = useToast();
  const { lang, holidayTypes: types, typesLoading } = useHeaderEditor();
  const [toAdd, setToAdd] = useState('');
  const menu = value;
  const items = menu.items || [];
  const manual = menu.source === 'manual';
  const onPatch = (p) => patch(PATH, p);
  const updateItems = (fn) => set(`${PATH}.items`, (list) => fn(list || []));

  const byId = useMemo(() => new Map((types || []).map((t) => [Number(t.id), t])), [types]);
  const rows = holidayTypeRows(menu, types, lang);
  const inactiveCount = (types || []).filter((t) => !isActiveType(t)).length;

  const addItem = () => {
    const id = Number(toAdd);
    if (!id) return;
    if (items.some((it) => it.holidayTypeId === id)) {
      showToast('That holiday type is already in the menu', 'error');
      return;
    }
    if (items.length >= MAX_MENU_ITEMS) {
      showToast(`The menu holds at most ${MAX_MENU_ITEMS} holiday types`, 'error');
      return;
    }
    updateItems((list) => [...list, { holidayTypeId: id, label: emptyText(), sub: emptyText(), icon: '' }]);
    setToAdd('');
    showToast(`${byId.get(id)?.name || 'Holiday type'} added to the header menu`, 'success');
  };

  const removeItem = (index) => {
    const name = byId.get(items[index]?.holidayTypeId)?.name || 'Holiday type';
    updateItems((list) => list.filter((_, i) => i !== index));
    showToast(`${name} removed from the header menu`, 'success');
  };

  const patchItem = (holidayTypeId, p) =>
    updateItems((list) => list.map((x) => (x.holidayTypeId === holidayTypeId ? { ...x, ...p } : x)));

  const addOptions = (types || [])
    .filter((t) => !items.some((it) => it.holidayTypeId === Number(t.id)))
    .map((t) => ({ value: String(t.id), label: `${t.name}${isActiveType(t) ? '' : ' (inactive)'}` }));

  return (
    <SectionCard
      id="menu-holidayTypes"
      nested
      icon={Palmtree}
      tone="orange"
      title="Holiday types menu"
      description="A dropdown of holiday types, each opening its holiday page. Only active types are ever shown."
      enabled={menu.enabled}
      onToggle={(enabled) => onPatch({ enabled })}
      showOn={menu.showOn}
      onShowOn={(showOn) => onPatch({ showOn })}
      errorCount={errorCount}
      previewTarget="menu:holidayTypes"
    >
      <div className={styles.grid3Wide}>
        <BilingualInput
          label="Menu label"
          value={menu.label}
          defaults={WEBSITE_DEFAULTS.holidayTypesLabel}
          onChange={(label) => onPatch({ label })}
        />
        <div className={styles.field}>
          <label className={styles.fieldLabel}>Filled from</label>
          <Segmented
            value={menu.source}
            ariaLabel="Holiday types source"
            options={[
              { value: 'auto', label: 'Automatic' },
              { value: 'manual', label: 'Hand-picked' },
            ]}
            onChange={(source) => onPatch({ source })}
          />
        </div>
        <TextField
          label="Max rows shown"
          type="number"
          value={menu.maxItems}
          inputProps={{ min: 1, max: MAX_MENU_ITEMS }}
          onChange={(v) => onPatch({ maxItems: Math.min(MAX_MENU_ITEMS, Math.max(1, parseInt(v, 10) || 1)) })}
        />
      </div>

      <BilingualInput
        label="Sub-label under every row"
        value={menu.sub}
        maxLength={MAX_SUB}
        defaults={WEBSITE_DEFAULTS.exploreHolidays}
        hint={manual ? 'A row can set its own below.' : 'Automatic rows all use this one.'}
        onChange={(sub) => onPatch({ sub })}
      />

      {!manual && (
        <p className={styles.hint}>
          Every active holiday type, in the order of Geo Data → Holiday Types.
          {inactiveCount > 0 && ` ${inactiveCount} inactive type${inactiveCount === 1 ? ' is' : 's are'} left out.`}
        </p>
      )}

      {manual && (
        <div className={styles.itemList}>
          {items.length === 0 && <p className={styles.emptyNote}>No holiday types picked yet. Add one below.</p>}
          {items.map((it, index) => {
            const type = byId.get(it.holidayTypeId);
            const live = isActiveType(type);
            const shown = rows.some((r) => r.id === it.holidayTypeId);
            const name = type?.name || (typesLoading ? 'Loading…' : `Unknown type #${it.holidayTypeId}`);
            return (
              <div key={it.holidayTypeId} className={`${styles.itemCard} ${live || typesLoading ? '' : styles.itemCardDead}`}>
                <div className={styles.itemTop}>
                  <span className={styles.orderIndex}>{index + 1}</span>
                  <TravelIconPicker
                    kind="emoji"
                    label="Row icon"
                    value={isEmoji(it.icon) ? it.icon : ''}
                    autoTile={(size) => <BuiltInTile icon={holidayTypeIcon(type?.name)} size={size} />}
                    autoLabel="Built-in icon"
                    autoHint="The website's own icon, chosen from the type's name"
                    onChange={(icon) => patchItem(it.holidayTypeId, { icon })}
                  />
                  <div className={styles.itemTitle}>
                    <strong>{name}</strong>
                    {!typesLoading && !live && (
                      <span className={styles.statusOff}>
                        {type ? 'Inactive: hidden on website' : 'Deleted: hidden on website'}
                      </span>
                    )}
                    {live && !shown && menu.enabled && <span className={styles.muted}>Over the max: not shown</span>}
                    {live && shown && type.slug && <span className={styles.muted}>Opens /holidays/{type.slug}</span>}
                  </div>
                  <RowActions
                    index={index}
                    count={items.length}
                    onMove={(i, dir) => updateItems((list) => moveItem(list, i, dir))}
                    onRemove={removeItem}
                  />
                </div>
                <div className={styles.grid2}>
                  <BilingualInput
                    label="Label"
                    value={it.label}
                    defaults={type?.name || undefined}
                    onChange={(label) => patchItem(it.holidayTypeId, { label })}
                    compact
                  />
                  <BilingualInput
                    label="Sub-label"
                    value={it.sub}
                    maxLength={MAX_SUB}
                    defaults={{ nl: pickText(menu.sub, 'nl', WEBSITE_DEFAULTS.exploreHolidays), en: pickText(menu.sub, 'en', WEBSITE_DEFAULTS.exploreHolidays) }}
                    onChange={(sub) => patchItem(it.holidayTypeId, { sub })}
                    compact
                  />
                </div>
              </div>
            );
          })}

          <div className={styles.addRow}>
            <div className={styles.addRowPicker}>
              <SelectBox
                width="100%"
                placeholder={typesLoading ? 'Loading holiday types…' : 'Choose a holiday type to add'}
                value={toAdd}
                options={addOptions}
                disabled={typesLoading || items.length >= MAX_MENU_ITEMS}
                onChange={(e) => setToAdd(e.target.value)}
              />
            </div>
            <button type="button" className={styles.primaryBtn} onClick={addItem} disabled={!toAdd}>
              <Plus size={14} /> Add
            </button>
          </div>
        </div>
      )}

      <WebsiteShows
        rows={rows}
        enabled={menu.enabled}
        loading={typesLoading}
        emptyText="Nothing: the menu is left out of the bar"
      />

      <FooterLinkEditor
        value={menu.footerLink}
        errorPath={`${PATH}.footerLink.url`}
        onChange={(footerLink) => onPatch({ footerLink })}
      />
    </SectionCard>
  );
}
